/*
 * STORE FACTORY — the single swap point for the whole data layer.
 * ============================================================================
 * Selects the client by whether a BRIDGE URL is configured:
 *
 *   - EngineStoreClient  → when `import.meta.env.VITE_SKIPPY_BRIDGE` is set at
 *     build time, OR `localStorage.getItem('skippyBridge')` is set at runtime
 *     (the runtime override wins, so a dev can point a shipped build at a local
 *     bridge without rebuilding). Backed by fetch() against the engine/store
 *     bridge (see standalone/BRIDGE-CONTRACT.md).
 *   - MockStoreClient    → otherwise (in-repo fixtures, so the offline shell
 *     still builds + renders with no backend).
 *
 * Singleton either way — computed once on first getStore().
 */

import type { StoreClient } from './StoreClient';
import { MockStoreClient } from './MockStoreClient';
import { EngineStoreClient } from './EngineStoreClient';

let client: StoreClient | null = null;

/** Resolve the configured bridge URL, runtime override first, else build-time env. */
function resolveBridgeUrl(): string | null {
  // Runtime override (a shipped build can be pointed at a local bridge).
  try {
    if (typeof localStorage !== 'undefined') {
      const ls = localStorage.getItem('skippyBridge');
      if (ls && ls.trim() !== '') return ls.trim();
    }
  } catch {
    // localStorage can throw (private mode / no DOM) — ignore and fall through.
  }
  // Build-time env.
  const env = import.meta.env.VITE_SKIPPY_BRIDGE;
  if (env && env.trim() !== '') return env.trim();
  return null;
}

export function getStore(): StoreClient {
  if (!client) {
    const bridgeUrl = resolveBridgeUrl();
    client = bridgeUrl ? new EngineStoreClient(bridgeUrl) : new MockStoreClient();
  }
  return client;
}

export type { StoreClient };
export { EngineStoreClient, BridgeError } from './EngineStoreClient';
export type { BridgeErrorKind } from './EngineStoreClient';
